import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import {
    FaEdit,
    FaEye,
} from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';
import SmartTable from '../../components/Smart/SmartTable';
import API from '../../api/axios';
import { useFormat } from '../../components/FormatContext';
import useCompanyContext from '../../hooks/useCompanyContext';

const SEARCH_TYPE = [
    {
        key: 'targetName',
        label: 'Shop Name',
    },
    {
        key: 'targetCity',
        label: 'Shop City',
    },
];

const DATE_OPTIONS = [
    'validStartDate',
    'validEndDate',
    'rentStartDate',
    'rentEndDate',
];

const FILTER_OPTIONS = {
    type: [
        'SHOP',
        'LOCATION',
        'CITY',
        'COUNTRY',
    ],
};

const DEFAULT_FILTERS = {
    dateFilters: [],
    type: [],
};

const DEFAULT_META = {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
};

const DEFAULT_PAGE = {
    page_number: 1,
    total_pages: 1,
};

const DiscountsList = () => {
    const {
        baseFormatters,
    } = useFormat();

    const {
        currentCompanyID,
        hasSpecificCompany,
    } = useCompanyContext();

    const [page, setPage] =
        useState(DEFAULT_PAGE);

    const [rows, setRows] =
        useState([]);

    const [meta, setMeta] =
        useState(DEFAULT_META);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [
        filters,
        setFilters,
    ] = useState(DEFAULT_FILTERS);

    const companyScopeKey =
        hasSpecificCompany
            ? String(currentCompanyID)
            : 'ALL';

    const previousCompanyScopeRef =
        useRef(companyScopeKey);

    const fetchOffers =
        useCallback(async () => {
            try {
                setLoading(true);
                setError('');

                const companyChanged =
                    previousCompanyScopeRef.current !==
                    companyScopeKey;

                const requestedPage =
                    companyChanged
                        ? 1
                        : page.page_number || 1;

                previousCompanyScopeRef.current =
                    companyScopeKey;

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(requestedPage),
                );

                params.set(
                    'pageSize',
                    String(
                        meta.pageSize ||
                        10,
                    ),
                );

                if (hasSpecificCompany) {
                    params.set(
                        'supplierId',
                        String(
                            currentCompanyID,
                        ),
                    );
                }

                searchConditions.forEach(
                    ({
                        type,
                        value,
                    }) => {
                        if (
                            value == null ||
                            String(value).trim() ===
                            ''
                        ) {
                            return;
                        }

                        const normalizedValue =
                            String(
                                value,
                            ).trim();

                        if (
                            type ===
                            'targetName'
                        ) {
                            params.set(
                                'targetName',
                                normalizedValue,
                            );
                        }

                        if (
                            type ===
                            'targetCity'
                        ) {
                            params.set(
                                'targetCity',
                                normalizedValue,
                            );
                        }

                        if (
                            type ===
                            'creator'
                        ) {
                            params.set(
                                'creator',
                                normalizedValue,
                            );
                        }
                    },
                );

                filters.dateFilters.forEach(
                    ({
                        type,
                        operator,
                        values = [],
                    }) => {
                        if (!type) {
                            return;
                        }

                        const value1 =
                            values[0];

                        const value2 =
                            values[1];

                        switch (operator) {
                            case 'between':
                                if (value1) {
                                    params.set(
                                        `${type}From`,
                                        value1,
                                    );
                                }

                                if (value2) {
                                    params.set(
                                        `${type}To`,
                                        value2,
                                    );
                                }
                                break;

                            case 'before':
                                if (value1) {
                                    params.set(
                                        `${type}Before`,
                                        value1,
                                    );
                                }
                                break;

                            case 'after':
                                if (value1) {
                                    params.set(
                                        `${type}After`,
                                        value1,
                                    );
                                }
                                break;

                            default:
                                break;
                        }
                    },
                );

                filters.type.forEach(
                    (type) => {
                        if (type) {
                            params.append(
                                'type',
                                type,
                            );
                        }
                    },
                );

                const queryString =
                    params.toString();

                const url =
                    queryString
                        ? `/offer?${queryString}`
                        : '/offer';

                const { data } =
                    await API.get(url);

                setRows(
                    data?.data || [],
                );

                const nextMeta =
                    data?.meta ||
                    DEFAULT_META;

                setMeta(nextMeta);

                const nextPage =
                    nextMeta.page || 1;

                const nextTotalPages =
                    nextMeta.totalPages ||
                    1;

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number:
                            nextPage,
                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (e) {
                setError(
                    e?.response?.data
                        ?.message ||
                    e?.message ||
                    '加载失败',
                );
            } finally {
                setLoading(false);
            }
        }, [
            companyScopeKey,
            currentCompanyID,
            hasSpecificCompany,
            page.page_number,
            meta.pageSize,
            searchConditions,
            filters.dateFilters,
            filters.type,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (valueOrUpdater) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (valueOrUpdater) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    useEffect(() => {
        fetchOffers();
    }, [fetchOffers]);

    const columns = useMemo(
        () => [
            {
                key: 'OfferTarget',
                label: 'Coverage',
                render: (item) => {
                    const targets =
                        Array.isArray(
                            item.OfferTarget,
                        )
                            ? item.OfferTarget
                            : [];

                    if (
                        !targets.length
                    ) {
                        return (
                            <span className="text-muted">
                                —
                            </span>
                        );
                    }

                    const cityMap =
                        targets.reduce(
                            (
                                acc,
                                target,
                            ) => {
                                const city =
                                    (
                                        target.location ||
                                        'Unknown'
                                    ).trim();

                                acc[city] =
                                    (
                                        acc[
                                        city
                                        ] ||
                                        0
                                    ) + 1;

                                return acc;
                            },
                            {},
                        );

                    const cityList =
                        Object.entries(
                            cityMap,
                        )
                            .map(
                                ([
                                    city,
                                    count,
                                ]) => ({
                                    city,
                                    count,
                                }),
                            )
                            .sort(
                                (
                                    a,
                                    b,
                                ) =>
                                    b.count -
                                    a.count,
                            );

                    const cityCount =
                        cityList.length;

                    const shopCount =
                        targets.length;

                    const cityLabel =
                        cityCount === 1
                            ? 'city'
                            : 'cities';

                    const shopLabel =
                        shopCount === 1
                            ? 'shop'
                            : 'shops';

                    const preview =
                        cityList.slice(
                            0,
                            3,
                        );

                    const remaining =
                        cityList.length -
                        preview.length;

                    return (
                        <div
                            style={{
                                maxWidth:
                                    320,
                            }}
                        >
                            <div className="small fw-semibold">
                                {
                                    cityCount
                                }{' '}
                                {
                                    cityLabel
                                }{' '}
                                /{' '}
                                {
                                    shopCount
                                }{' '}
                                {
                                    shopLabel
                                }
                            </div>

                            <div className="text-muted small">
                                {preview
                                    .map(
                                        (
                                            city,
                                        ) =>
                                            `${city.city} (${city.count})`,
                                    )
                                    .join(
                                        ', ',
                                    )}

                                {remaining >
                                    0
                                    ? ` +${remaining} more`
                                    : ''}
                            </div>
                        </div>
                    );
                },
            },
            {
                keys: [
                    'id',
                    'validStartDate',
                    'validEndDate',
                ],
                label:
                    'Validity Period',
                render: (item) => (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                {item.id}
                            </Tooltip>
                        }
                    >
                        <div className="d-flex flex-column">
                            <span>
                                {baseFormatters.date.numeric.format(
                                    new Date(
                                        item.validStartDate,
                                    ),
                                )}
                            </span>

                            <span>
                                {baseFormatters.date.numeric.format(
                                    new Date(
                                        item.validEndDate,
                                    ),
                                )}
                            </span>
                        </div>
                    </OverlayTrigger>
                ),
            },
            {
                keys: [
                    'rentStartDate',
                    'rentEndDate',
                ],
                label:
                    'Rental Period',
                render: (item) => (
                    <div className="d-flex flex-column">
                        <span>
                            {baseFormatters.date.numeric.format(
                                new Date(
                                    item.rentStartDate,
                                ),
                            )}
                        </span>

                        <span>
                            {baseFormatters.date.numeric.format(
                                new Date(
                                    item.rentEndDate,
                                ),
                            )}
                        </span>
                    </div>
                ),
            },
            {
                keys: [
                    'rmbDiscount',
                ],
                label:
                    'Total Discount',
                render: (item) => (
                    <span>
                        {Number(
                            item.rmbDiscount,
                        ) +
                            Number(
                                item.supplierDiscount,
                            )}
                        %
                    </span>
                ),
            },
            {
                keys: [
                    'supplierDiscount',
                ],
                label:
                    'Discount Supplier',
                render: (item) => (
                    <span>
                        {
                            item.supplierDiscount
                        }
                        %
                    </span>
                ),
            },
            {
                key: 'minDay',
                label: 'Min Days',
                render: (item) => (
                    <span>
                        {item.minDay}
                    </span>
                ),
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={`/discounts/edit/${item.id}`}
                            className="btn btn-view p-0"
                            title="View / Edit"
                        >
                            <FaEye
                                size={20}
                            />
                        </NavLink>

                        <NavLink
                            to={`/discounts/edit/${item.id}`}
                            className="btn btn-edit p-0"
                            title="Edit"
                        >
                            <FaEdit
                                size={20}
                                style={{
                                    marginLeft:
                                        '4px',
                                }}
                            />
                        </NavLink>
                    </div>
                ),
            },
        ],
        [baseFormatters],
    );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Discounts (Offers)
                        List
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to="/dashboard"
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Offers
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={10}>
                                    <SmartTopTool
                                        searchType={
                                            SEARCH_TYPE
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        dateOptions={
                                            DATE_OPTIONS
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                    />
                                </Col>

                                <Col
                                    md={2}
                                    className="d-flex justify-content-end"
                                >
                                    <NavLink
                                        to="/discounts/new"
                                        className="border ms-1 btn-custom-success btn btn-primary"
                                        title="New"
                                        style={{
                                            height:
                                                '40px',
                                        }}
                                    >
                                        + New
                                    </NavLink>
                                </Col>

                                <Col md={12}>
                                    <SmartTable
                                        data={
                                            rows
                                        }
                                        columns={
                                            columns
                                        }
                                        loading={
                                            loading
                                        }
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {error ||
                                                        'No matching offers found'}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex justify-content-end"
                                >
                                    <SmartPagination
                                        page={
                                            page
                                        }
                                        setPage={
                                            setPage
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default DiscountsList;